import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import ChatMessage from "../../Components/ChatMessage/ChatMessage";
import FeatureRequestSuggestions from "../../Components/FeatureRequestSuggestions/FeatureRequestSuggestions";
import Form from "../../Components/Form/Form";
import SurveyIntro from "../../Components/SurveyIntro/SurveyIntro";
import WidgetPoweredBy from "../../Components/WidgetPoweredBy/WidgetPoweredBy";
import { useConfigStore } from "../../Stores/Config";
import { getFirstFormQuestion, useFormStore } from "../../Stores/Form";
import { AppMode, useRouterStore } from "../../Stores/Router";
import "./FeedbackFlow.scss";
import CommunicationManager from "../../Helper/CommunicationManager";
import { scrollAnchorToBottom } from "../../Helper/ScrollHelper";
import { resetWidget } from "../../Components/WidgetHeader/WidgetHeader";

// One rendered line of the chat-style transcript. `isCreator` is the side it
// sits on — the only thing bubble grouping depends on here, since the flow only
// ever has two speakers.
type TranscriptEntry = {
  key: string;
  isCreator: boolean;
  /**
   * False for entries that bring their OWN surface instead of a bubble (the
   * feature-request duplicates card). They never group: a neighbouring bubble
   * tightening a corner against a card would point at nothing.
   */
  groupable?: boolean;
  render: (flags: {
    firstOfGroup: boolean;
    lastOfGroup: boolean;
  }) => ReactNode;
};

function FeedbackFlow() {
  const routerStore = useRouterStore();
  const [currentAction, setCurrentAction] = useState(null as any);
  const formStore = useFormStore();
  const configStore = useConfigStore();
  const bottomRef = useRef(null as any);
  const hasChatStyle = formStore?.action?.chatStyle ?? false;
  const currentFormItems = formStore.action ? formStore.getCurrentFormItems() : [];
  const hasUnpromptedStep =
    currentFormItems.length > 0 && !getFirstFormQuestion(currentFormItems);
  const showInput =
    !hasChatStyle ||
    (!formStore.formSent &&
      (formStore.formAnswersLastType === "question" ||
        hasUnpromptedStep ||
        formStore.formError));

  const scrollToBottom = () => {
    // Scroll our own container, never `scrollIntoView` — that crosses the iframe
    // boundary and scrolls the host page on Safari (GLEAP widget scroll bug).
    scrollAnchorToBottom(bottomRef.current);

    setTimeout(() => {
      scrollAnchorToBottom(bottomRef.current);
    }, 1000);
  };

  useEffect(() => {
    scrollToBottom();
  }, [
    formStore.formAnswers,
    formStore.formError,
    formStore.formSent,
    formStore.duplicates,
  ]);

  useEffect(() => {
    if (currentAction) {
      const routerState = useRouterStore.getState();
      const formState = useFormStore.getState();

      // Check if we should preserve the draft.
      const currentFlowId = routerState.currentData?.flowId;
      const shouldPreserveDraft = formState.preserveForm;
      const hasDraftInCurrentFlow =
        !!formState.action &&
        formState.feedbackFlow === currentFlowId &&
        (Object.keys(formState.formData).length > 0 ||
          formState.formAnswers.length > 0 ||
          formState.currentStep > 0 ||
          formState.duplicates.length > 0);

      if (shouldPreserveDraft && hasDraftInCurrentFlow) {
        // Don't reset the form
        useFormStore.setState({
          preserveForm: false,
        });
        return;
      }

      useFormStore.setState({
        preserveForm: false,
      });
      formState.resetForm();
      formState.setFormAction(currentAction);
      formState.validateForm();
    }
  }, [currentAction]);

  const closeWidgetAndReset = () => {
    CommunicationManager.getInstance().sendMessage({
      name: "close-widget",
    });

    resetWidget();
  };

  useEffect(() => {
    const routeData = routerStore.currentData;
    if (!routeData || !routeData.flowId) {
      return;
    }
    let isCurrent = true;
    configStore
      .loadAction(routeData.flowId)
      .then((action: any) => {
        if (!isCurrent) {
          return;
        }
        if (!action) {
          closeWidgetAndReset();
          return;
        }
        setCurrentAction(action);
      })
      .catch(() => {
        if (isCurrent) {
          closeWidgetAndReset();
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [routerStore.currentData]);

  const renderProgressBar = () => {
    if (
      !currentAction?.showProgressBar ||
      routerStore.appMode === AppMode.WIDGET ||
      routerStore.appMode === AppMode.SURVEY ||
      formStore.formSent ||
      formStore.sendingForm
    ) {
      return null;
    }

    const progress = formStore.getProgress();
    if (progress === 0) {
      return null;
    }

    return (
      <div className="feedback-flow-progress-bar">
        <div
          className="feedback-flow-progress-bar-inner"
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  };

  const renderForm = () => {
    if (
      !hasChatStyle &&
      formStore.duplicates &&
      formStore.duplicates.length > 0
    ) {
      return (
        <div className="suggestions-container">
          <div className="suggestions-container--title">
            {configStore.translateText("suggestFeatureRequest")}
          </div>
          <FeatureRequestSuggestions />
        </div>
      );
    }

    if (routerStore.appMode === AppMode.WIDGET) {
      if (!showInput) {
        return;
      }

      return (
        <div className={`feedback-flow-inner-container`}>
          <div className="feedback-flow">
            <Form animated={false} />
          </div>
        </div>
      );
    }

    return (
      <div className="feedback-flow">
        {renderProgressBar()}
        <SurveyIntro currentAction={formStore.action} />
        <Form />
      </div>
    );
  };

  // The chat-style transcript, in render order. Built as data first so the
  // grouping pass below can see each entry's neighbours — the bubbles tighten
  // the corner facing the message next to them on their own side, and that is
  // only knowable once the whole sequence exists.
  const buildTranscript = (): TranscriptEntry[] => {
    if (!hasChatStyle) {
      return [];
    }

    const entries: TranscriptEntry[] = [];

    const agentMessage = (
      key: string,
      content: string,
      extraProps: { typingDelay?: number; forceTyping?: boolean } = {},
    ): TranscriptEntry => ({
      key,
      isCreator: false,
      render: ({ firstOfGroup, lastOfGroup }) => (
        <ChatMessage
          firstOfGroup={firstOfGroup}
          lastOfGroup={lastOfGroup}
          chatMessage={{ data: { content }, type: "TEXT" }}
          showTime={false}
          {...extraProps}
        />
      ),
    });

    if (currentAction.introMessage) {
      entries.push(agentMessage("lastStylePr", currentAction.introMessage));
    }

    formStore.formAnswers.forEach((item, index) => {
      if (item.type === "question") {
        entries.push(
          agentMessage(`question-${index}`, item.data, {
            typingDelay: item.delay,
          }),
        );
        return;
      }

      entries.push({
        key: `answer-${index}`,
        isCreator: true,
        render: ({ firstOfGroup, lastOfGroup }) => (
          <ChatMessage
            firstOfGroup={firstOfGroup}
            lastOfGroup={lastOfGroup}
            chatMessage={{ data: { content: item.data }, type: "TEXT" }}
            isCreator={true}
            showTime={false}
          />
        ),
      });
    });

    if (formStore.formSent) {
      entries.push(agentMessage("lastStyle", formStore.action.thanksMessage));
    }

    if (formStore.checkingDuplicates) {
      entries.push(
        agentMessage("lastDup", "Checking for duplicates...", {
          forceTyping: true,
        }),
      );
    }

    if (formStore.sendingForm) {
      entries.push(
        agentMessage("lastSend", "Sending message...", { forceTyping: true }),
      );
    }

    if (formStore.duplicates && formStore.duplicates.length > 0) {
      entries.push(
        agentMessage(
          "prelast",
          configStore.translateText("suggestFeatureRequest"),
        ),
      );
      entries.push({
        key: "lastmess",
        isCreator: false,
        groupable: false,
        render: () => (
          <ChatMessage
            firstOfGroup={true}
            lastOfGroup={true}
            chatMessage={{ data: {}, type: "FEATURE_REQUEST_DUPLICATES" }}
            showTime={false}
          />
        ),
      });
    }

    return entries;
  };

  // Same grouping convention as the conversation timeline: a run of messages
  // from one side reads as one block of speech, and a change of speaker (or a
  // card, which owns no bubble) ends it.
  const renderTranscript = () => {
    const entries = buildTranscript();

    return entries.map((entry, index) => {
      const previous = entries[index - 1];
      const next = entries[index + 1];
      const opensGroup =
        entry.groupable === false ||
        !previous ||
        previous.groupable === false ||
        previous.isCreator !== entry.isCreator;
      const closesGroup =
        entry.groupable === false ||
        !next ||
        next.groupable === false ||
        next.isCreator !== entry.isCreator;

      return (
        <Fragment key={entry.key}>
          {entry.render({
            firstOfGroup: opensGroup,
            lastOfGroup: closesGroup,
          })}
        </Fragment>
      );
    });
  };

  return (
    <div
      className={`feedback-flow-container ${
        hasChatStyle
          ? "feedback-flow-container--chat-style"
          : "feedback-flow-container--form-style"
      }`}
    >
      {currentAction && routerStore.appMode === AppMode.WIDGET && (
        <div
          className={`feedback-flow-chat ${
            showInput && "feedback-flow-chat--has-input"
          }`}
        >
          <div
            style={{
              flexGrow: 1,
            }}
          ></div>
          {renderTranscript()}
          <WidgetPoweredBy />
          <div ref={bottomRef} />
        </div>
      )}
      {renderForm()}
    </div>
  );
}

export default FeedbackFlow;
